import { track } from '@app/analytics/client';
import { FEATURE_DESCRIPTIONS, isFeatureName, type PlanFeatures } from '@app/billing-config';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@app/ui';
import { FadeIn, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { BrandLink } from '../components/BrandLink';
import { RoadmapSection } from '../components/roadmap-section';
import { RouteErrorComponent } from '../components/route-error';
import { SiteFooter } from '../components/site-footer';

const pricingSearchSchema = z.object({
  // Set when the server's feature-gate middleware (or a proactive client
  // gate) redirects the user here. We render an upgrade modal keyed off
  // this value. Use `.catch` so an unknown feature name silently degrades
  // to the plain pricing page rather than throwing a route validation error.
  limit: z
    .string()
    .optional()
    .transform((v) => (v && isFeatureName(v) ? (v as keyof PlanFeatures) : undefined))
    .catch(undefined),
});

export const Route = createFileRoute('/pricing')({
  validateSearch: pricingSearchSchema,
  component: Pricing,
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
});

function Pricing() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const limitFeature = search.limit;

  useEffect(() => {
    // `source` would ideally come from a referrer/query param; for now we
    // pass null and infer from $referrer/utm_* properties on PostHog's side.
    track('pricing_viewed', { source: null, current_plan: null });
  }, []);

  useEffect(() => {
    if (limitFeature) {
      track('feature_gate_hit', {
        feature: limitFeature,
        current_plan: 'free',
        suggested_plan: FEATURE_DESCRIPTIONS[limitFeature].suggestedPlan,
        surface: 'ui',
      });
    }
  }, [limitFeature]);

  const dismissModal = () =>
    navigate({ search: (prev) => ({ ...prev, limit: undefined }), replace: true });

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b">
        <BrandLink />
        <div className="flex gap-2 sm:gap-3">
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button variant="outline" className="min-h-[44px]">
              Sign In
            </Button>
          </Link>
          <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
            <Button className="min-h-[44px]">Get Started Free</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h1 className="text-3xl sm:text-4xl font-bold text-center mb-2">
              Pricing built for every NIL stakeholder
            </h1>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-lg text-muted-foreground text-center mb-12">
              Three revenue engines. One flywheel.
            </p>
          </FadeIn>

          {/* Athletes */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-2">Athletes</h2>
            <p className="text-muted-foreground mb-6">
              Free to start. Upgrade when you outgrow the 5-deal limit.
            </p>
            <StaggerContainer className="grid md:grid-cols-2 gap-6">
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Free</CardTitle>
                    <CardDescription>For athletes just getting started.</CardDescription>
                    <p className="text-3xl font-bold mt-2">$0</p>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <ul className="space-y-1 text-muted-foreground list-disc pl-5">
                      <li>Up to 5 active deals</li>
                      <li>Manual deal logging</li>
                      <li>Payment & deliverable tracking</li>
                      <li>3 AI screenshot extractions / month</li>
                    </ul>
                    <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
                      <Button className="w-full mt-3">Get started free</Button>
                    </Link>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full border-primary/40">
                  <CardHeader>
                    <CardTitle>Pro</CardTitle>
                    <CardDescription>For athletes managing multiple deals.</CardDescription>
                    <p className="text-3xl font-bold mt-2">
                      $10
                      <span className="text-base font-normal text-muted-foreground">/month</span>
                    </p>
                    <p className="text-xs text-muted-foreground">or $96/year (save 20%)</p>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <ul className="space-y-1 text-muted-foreground list-disc pl-5">
                      <li>Unlimited deals</li>
                      <li>Unlimited AI extraction + contract summaries</li>
                      <li>AI deal valuation</li>
                      <li>Brand matching & advanced analytics</li>
                      <li>Data export + SMS notifications</li>
                    </ul>
                    <Link
                      to="/checkout/$plan"
                      params={{ plan: 'pro' }}
                      search={{ annual: false }}
                      onClick={() =>
                        track('pricing_cta_clicked', {
                          plan: 'pro',
                          billing_interval: 'monthly',
                          current_plan: null,
                          position: 'athlete_pro',
                        })
                      }
                    >
                      <Button className="w-full mt-3">Start Pro</Button>
                    </Link>
                  </CardContent>
                </Card>
              </StaggerItem>
            </StaggerContainer>
          </section>

          {/* Agents */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-2">Agents</h2>
            <p className="text-muted-foreground mb-6">
              Manage your whole roster from one dashboard. Commission tracking included.
            </p>
            <StaggerContainer className="grid md:grid-cols-3 gap-6">
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Solo</CardTitle>
                    <CardDescription>For independent agents managing up to 10 athletes.</CardDescription>
                    <p className="text-3xl font-bold mt-2">
                      $300
                      <span className="text-base font-normal text-muted-foreground">/month</span>
                    </p>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    <ul className="space-y-1 list-disc pl-5">
                      <li>Multi-athlete dashboard</li>
                      <li>Commission tracking & reconciliation</li>
                      <li>Client reporting</li>
                      <li>Full AI feature access</li>
                    </ul>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Team</CardTitle>
                    <CardDescription>For small agencies — up to 50 athletes, 5 seats.</CardDescription>
                    <p className="text-3xl font-bold mt-2">
                      $500
                      <span className="text-base font-normal text-muted-foreground">/month</span>
                    </p>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    <ul className="space-y-1 list-disc pl-5">
                      <li>Everything in Solo</li>
                      <li>Team collaboration (5 seats)</li>
                      <li>White-label reports</li>
                      <li>API access, priority support</li>
                    </ul>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Enterprise</CardTitle>
                    <CardDescription>For 50+ athletes. Custom integrations & support.</CardDescription>
                    <p className="text-3xl font-bold mt-2">Custom</p>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <a href="mailto:sales@nilboard.app?subject=Agent%20Enterprise%20inquiry">
                      <Button variant="outline" className="w-full mt-2">
                        Talk to sales
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </StaggerItem>
            </StaggerContainer>
          </section>

          {/* Universities */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-2">Universities</h2>
            <p className="text-muted-foreground mb-6">
              Per-athlete pricing. Athletes get full Pro features at no cost to them.
            </p>
            <StaggerContainer className="grid md:grid-cols-3 gap-6">
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Starter</CardTitle>
                    <CardDescription>Up to 100 athletes.</CardDescription>
                    <p className="text-3xl font-bold mt-2">
                      $10
                      <span className="text-base font-normal text-muted-foreground">
                        /athlete/mo
                      </span>
                    </p>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    <ul className="space-y-1 list-disc pl-5">
                      <li>FERPA-safe compliance dashboard</li>
                      <li>Aggregate reporting + audit logs</li>
                      <li>2 admin seats, email support</li>
                      <li>Athletes get full Pro features</li>
                    </ul>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Standard</CardTitle>
                    <CardDescription>101–300 athletes.</CardDescription>
                    <p className="text-3xl font-bold mt-2">
                      $7
                      <span className="text-base font-normal text-muted-foreground">
                        /athlete/mo
                      </span>
                    </p>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    <ul className="space-y-1 list-disc pl-5">
                      <li>Everything in Starter</li>
                      <li>Custom branding + SSO</li>
                      <li>5 admin seats</li>
                      <li>Guided onboarding, chat support</li>
                    </ul>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Enterprise</CardTitle>
                    <CardDescription>301+ athletes.</CardDescription>
                    <p className="text-3xl font-bold mt-2">
                      $5
                      <span className="text-base font-normal text-muted-foreground">
                        /athlete/mo
                      </span>
                    </p>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <ul className="space-y-1 list-disc pl-5 text-muted-foreground mb-3">
                      <li>Everything in Standard</li>
                      <li>API + custom integrations</li>
                      <li>Dedicated CSM, 99.9% SLA</li>
                    </ul>
                    <a href="mailto:sales@nilboard.app?subject=University%20Enterprise%20inquiry">
                      <Button variant="outline" className="w-full">
                        Talk to sales
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </StaggerItem>
            </StaggerContainer>
          </section>

          {/* Marketplace fees */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-2">Marketplace fees</h2>
            <p className="text-muted-foreground mb-6">
              Subscription fees and marketplace fees are independent. You can use the free tier
              and still participate in the marketplace — and vice versa.
            </p>
            <StaggerContainer className="grid md:grid-cols-2 gap-6">
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>T1 · Athlete → Brand</CardTitle>
                    <CardDescription>
                      When an athlete sources a deal themselves using NilBoard tools.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>
                      A small take-rate at closing — well below the industry's 20% standard — so
                      athletes keep more of what they earn while NilBoard stays sustainable.
                    </p>
                    <p>Exact rate is shown at deal creation time.</p>
                  </CardContent>
                </Card>
              </StaggerItem>
              <StaggerItem>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>T2 · Brand → Athlete</CardTitle>
                    <CardDescription>
                      When a brand discovers a vetted athlete through NilBoard's marketplace.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>
                      A larger take-rate at closing plus a residual on repeat deals from the same
                      brand within a defined window — compensating NilBoard for the match.
                    </p>
                    <p>Exact rate and residual window are shown at deal creation time.</p>
                  </CardContent>
                </Card>
              </StaggerItem>
            </StaggerContainer>
            <p className="text-center text-sm text-muted-foreground mt-6">
              See the{' '}
              <Link to="/terms" className="underline">
                Marketplace Fees section of our Terms
              </Link>{' '}
              for the full fee schedule and change-notice policy.
            </p>
          </section>

          {/* Roadmap / Coming Soon */}
          <RoadmapSection />

          {/* Footer CTA */}
          <section className="text-center border-t pt-10">
            <h3 className="text-xl font-semibold mb-3">Ready to start?</h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/auth/login" search={{ returnUrl: '/dashboard' }}>
                <Button size="lg">Get started free</Button>
              </Link>
              <a href="mailto:sales@nilboard.app?subject=NilBoard%20sales%20inquiry">
                <Button size="lg" variant="outline">
                  Talk to sales
                </Button>
              </a>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />

      <FeatureLimitModal feature={limitFeature} onDismiss={dismissModal} />
    </div>
  );
}

interface FeatureLimitModalProps {
  feature: keyof PlanFeatures | undefined;
  onDismiss: () => void;
}

function FeatureLimitModal({ feature, onDismiss }: FeatureLimitModalProps) {
  // AlertDialog stays mounted but only opens when a feature is set, so
  // closing it doesn't unmount the underlying pricing page.
  const open = feature != null;
  const desc = feature ? FEATURE_DESCRIPTIONS[feature] : null;

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onDismiss()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {desc ? `You've reached the limit for ${desc.label}` : 'Upgrade needed'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {desc ? (
              <>
                {desc.freeLimitCopy} {desc.proBenefitCopy}
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDismiss}>Not now</AlertDialogCancel>
          {desc ? (
            <Link
              to="/checkout/$plan"
              params={{ plan: desc.suggestedPlan }}
              search={{ annual: false }}
              onClick={() =>
                track('upgrade_cta_clicked', {
                  source: 'feature_gate_block',
                  current_plan: null,
                  target_plan: desc.suggestedPlan,
                })
              }
            >
              <AlertDialogAction>Upgrade to {desc.suggestedPlan === 'pro' ? 'Pro' : desc.suggestedPlan}</AlertDialogAction>
            </Link>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
